<template>
  <section
    v-if="status === 'success'"
    class="world"
  >
    <SectionTitle
      :title="`周辺の${TARGET2NAME[target]}の天気`"
    />
    <div
      class="worldList"
    >
      <NuxtLink
        v-for="pointId in pointIdList"
        :key="pointId"
        :to="isExternalLink ? `${runtimeConfig.app.baseURL}${pointId.replace(/^\//, '')}` : pointId"
        :external="isExternalLink"
        class="link"
      >
        <template v-if="data.info[pointId]">
          <div class="text">
            <figure class="flag">
              <img
                :src="`${runtimeConfig.public.gvsUrl}/onebox/img/world/flag/${data.info[pointId].cc}.webp`"
                width="20"
                height="15"
                alt=""
              >
            </figure>
            <div class="inner">
              <p class="area">
                {{ data.info[pointId].point_ja }}
              </p>
              <p class="time">
                {{ dayjs.unix(now).tz(data.info[pointId].timezone).format("H時") }}
              </p>
            </div>
          </div>
          <div class="info">
            <figure class="icon">
              <img
                :src="'https://gvs.weathernews.jp/onebox/img/wxicon/' + data.wx[pointId].wx + '.webp'"
                width="34"
                height="25"
                loading="lazy"
                alt=""
              >
            </figure>
            <div class="temp">
              <p class="max">
                {{ data.wx[pointId].maxt }}℃
              </p>
              <p class="min">
                {{ data.wx[pointId].mint }}℃
              </p>
            </div>
          </div>
        </template>
      </NuxtLink>
    </div>
  </section>
</template>

<script setup>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

const props = defineProps({
  now: {
    type: Number,
    required: true
  },
  targetPointId: {
    type: String,
    required: true
  },
  pointIdList: {
    type: Array,
    default: () => []
  },
  target: {
    type: String,
    required: true
  }
})

const TARGET2NAME = {
  city: '都市',
  landmark: '観光地'
}

const { data, status } = await useAsyncData(
  `PointSurroundingCityWxTable:${props.target}:${props.targetPointId}`,
  async () => {
    const infoDataTask = props.pointIdList.length > 0 ? $fetch(`/api/point/info?pointId=${props.pointIdList.join(',')}`) : {}
    const wxDataTask = props.pointIdList.length > 0 ? $fetch(`/api/point/weather?pointId=${props.pointIdList.join(',')}`) : {}

    const [infoData, wxData] = await Promise.all([infoDataTask, wxDataTask])
    const formattedWxData = formatMajorCityWeather(props.pointIdList, wxData, props.now)

    return {
      info: infoData,
      wx: formattedWxData
    }
  }
)

const runtimeConfig = useRuntimeConfig()
const { $isWebViewFlag: isWebViewFlag } = useNuxtApp()
const isExternalLink = ref(isWebViewFlag)
</script>

<style lang="scss" scoped>
.world {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.worldList {
  display: flex;
  flex-wrap: wrap;
  border-left: 1px solid #CCC;
  .link {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 5px;
    width: 50%;
    padding: 6px;
    background-color: #FFF;
    border-bottom: 1px solid #CCC;
    border-right: 1px solid #CCC;
    &:first-child,
    &:nth-child(2) {
      border-top: 1px solid #CCC;
    }
  }
  .text {
    display: flex;
    align-items: center;
    gap: 3px;
    .inner {
      display: flex;
      flex-direction: column;
      gap: 4px;
      flex: 1;
    }
    .flag {
      aspect-ratio: 1;
      border: 1px solid #ccc;
      border-radius: 50%;
      overflow: hidden;
      img {
        display: block;
        filter: saturate(60%) brightness(130%);
        width: 20px;
        height: 100%;
        object-fit: cover;
      }
    }
    .area {
      color: #3569C0;
      font-size: 12px;
      font-weight: 600;
      line-height: 14px;
      text-decoration: underline;
    }
    .time {
      color: #606060;
      font-size: 10px;
      line-height: 1em;
    }
  }
  .info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 3px;
    min-width: 63px;
    .icon {
      width: 30px;
      aspect-ratio: 34 / 25;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .temp {
      display: flex;
      flex-direction: column;
      gap: 3px;
      p {
        text-align: right;
        font-size: 12px;
        font-weight: 600;
        line-height: 1em;
        &.max {
          color: #F64D00;
        }
        &.min {
          color: #0075F3;
        }
      }
    }
  }
  @media (any-hover: hover) {
    .link {
      transition: opacity .2s ease-in-out;
      &:hover {
        opacity: .6;
      }
    }
  }
}
.main {
  .worldList {
    @media screen and (min-width: 830px) {
      gap: 7px;
      border-top: none;
      border-left: none;
      .link {
        display: flex;
        align-items: center;
        gap: 8px;
        width: calc((100% - 14px) / 3);
        padding: 3px 8px;
        background-color: #FFF;
        border: 1px solid #CCC;
        border-radius: 3px;
      }
    }
  }
}
</style>
